import React, { useState, useContext, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../App";
import { storage, db } from '../firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { ArrowLeft, Calendar, Plus, Upload, Save, X } from "lucide-react";
import { getLatestAccountBalance } from "../utils/accountBalanceUtils";
import { getClipboardImageFiles, readImageFilesAsDataUrls } from "../utils/imageInput";
import { getPnlTone } from "../utils/pnlTone";
import { readUnifiedJournalBetaCache, writeUnifiedJournalBetaCache } from "../utils/journalMode";
import { ScreenshotUploadError, uploadScreenshotList } from "../utils/entryScreenshotUpload";
import { useToast } from "../components/ui";
import {
    CHART_DROP_HIGHLIGHT_STYLE,
    useChartDropZone,
    useDocumentImagePaste,
    useSubmitShortcut,
    useUnsavedChangesGuard,
} from "../utils/entryFormHooks";

const QuickAddPage = () => {
    const { currentUser, selectedAccount } = useContext(UserContext);
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [uploading, setUploading] = useState(false);
    const [entryMode, setEntryMode] = useState(() => (
        location.state?.entryMode === 'tape' ? 'tape' : 'trade'
    ));
    const [screenshots, setScreenshots] = useState([]);
    const [unifiedJournalBeta, setUnifiedJournalBeta] = useState(readUnifiedJournalBetaCache);
    const fileInputRef = useRef(null);
    const formRef = useRef(null);

    useEffect(() => {
        if (!currentUser) return;

        const loadJournalMode = async () => {
            try {
                const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
                const prefsSnap = await getDoc(prefsRef);
                const enabled = !prefsSnap.exists() || prefsSnap.data().unifiedJournalBeta !== false;
                setUnifiedJournalBeta(enabled);
                writeUnifiedJournalBetaCache(enabled);
            } catch (error) {
                console.error('Error loading Unified Journal beta setting:', error);
            }
        };

        loadJournalMode();
    }, [currentUser]);

    // Date management - use current date as default
    const [selectedDate, setSelectedDate] = useState(() => {
        const requestedDate = location.state?.selectedDate;
        if (/^\d{4}-\d{2}-\d{2}$/.test(requestedDate || '')) {
            return requestedDate;
        }

        // Default to current date
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });

    const [formData, setFormData] = useState({
        title: '',
        pnl: '',
        rr: '',
        notes: ''
    });

    const isDirty = screenshots.length > 0
        || Object.values(formData).some((value) => String(value).trim() !== '');
    const leaveIfConfirmed = useUnsavedChangesGuard(isDirty);

    // Helper: Calculate Balance
    const calculateAccountBalance = (previousBalance, pnlValue) => {
        const prev = Number(previousBalance) || 0;
        const pnlVal = Number(pnlValue) || 0;
        return (Math.round((prev + pnlVal) * 100) / 100).toFixed(2);
    };

    const handleFiles = async (files) => {
        const images = await readImageFilesAsDataUrls(files);
        if (images.length > 0) {
            setScreenshots((previous) => [...previous, ...images]);
        }
    };

    // Helper: Handle Images
    const handleFileUpload = (e) => {
        handleFiles(e.target.files);
        e.target.value = null;
    };

    const handlePasteImages = (e) => {
        const files = getClipboardImageFiles(e);
        if (files.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        handleFiles(files);
    };

    // Drag-and-drop onto the chart area and Ctrl/Cmd+V anywhere on the page both
    // add charts through the same preview/upload path as the file picker.
    const { isDragging: isDraggingChart, dropZoneProps: chartDropZoneProps } = useChartDropZone(handleFiles);
    useDocumentImagePaste(handleFiles);
    useSubmitShortcut(formRef);

    const formatDateDisplay = (dateValue) => {
        if (!dateValue) return "Select date";
        const [year, month, day] = dateValue.split("-");
        if (!year || !month || !day) return dateValue;
        return `${month}/${day}/${year}`;
    };

    const renderDateField = ({ id, testId }) => (
        <div className={`${subPanelClass} p-3 min-w-[220px] sm:min-w-[240px]`}>
            <label htmlFor={id} className={labelClass}>Date</label>
            <div className="relative mt-2 rounded-2xl">
                <div
                    data-testid={testId}
                    className={`${compactInputClass} theme-focus-within-accent pointer-events-none flex min-w-0 items-center justify-between gap-4 pr-4`}
                >
                    <span className="truncate">{formatDateDisplay(selectedDate)}</span>
                    <Calendar className="h-5 w-5 shrink-0 theme-text-muted" aria-hidden="true" />
                </div>
                <input
                    id={id}
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="absolute inset-0 h-full w-full min-w-0 cursor-pointer opacity-0"
                />
            </div>
        </div>
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        saveQuickEntry();
    };

    const saveQuickEntry = async ({ addAnother = false } = {}) => {
        if (uploading || !currentUser || !selectedAccount) return;
        if (entryMode === 'trade' && formData.pnl === '') return;

        setUploading(true);
        let imageUrls = [];

        try {
            const uploadBatchId = Date.now();
            try {
                imageUrls = await uploadScreenshotList(screenshots, async (src, index) => {
                    const imageRef = ref(
                        storage,
                        `screenshots/${currentUser.uid}/${selectedAccount.id}/${uploadBatchId}-${index}.jpg`
                    );
                    await uploadString(imageRef, src, 'data_url');
                    return getDownloadURL(imageRef);
                });
            } catch (uploadError) {
                if (!(uploadError instanceof ScreenshotUploadError)) throw uploadError;
                // Never save without the charts; keep the ones that uploaded so a retry only re-sends the rest.
                setScreenshots(uploadError.images);
                toast.error(`${uploadError.message} The entry was not saved. Click Save to try again, or remove the chart.`);
                return;
            }
            // Keep the uploaded URLs so retrying after a failed save does not upload twice.
            setScreenshots(imageUrls);

            const [year, month, day] = selectedDate.split('-').map(Number);
            const createdTimestamp = new Date().toISOString() + '-' + Math.random().toString(36).slice(2, 8);

            const isTape = entryMode === 'tape';
            const pnlNum = isTape ? 0 : Number(formData.pnl);
            const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
            const existingEntriesSnap = await getDocs(entriesCol);
            const existingEntries = existingEntriesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            const previousBalance = getLatestAccountBalance(existingEntries);
            
            const normalizedNotes = formData.notes.trim();

            const entry = {
                title: formData.title.trim() || (isTape ? "Quick Tape Reading" : "Quick Trade"),
                pnl: pnlNum,
                rr: isTape ? "0" : (formData.rr || "0"),
                screenshots: imageUrls,
                created: createdTimestamp,
                year: String(year),
                month: String(month),
                day: String(day),
                notes: normalizedNotes || (isTape ? 'Quick tape reading' : 'Quick add entry'),
                tickerTraded: "",
                tradeSession: "",
                tapeReading: isTape,
                accountBalance: calculateAccountBalance(previousBalance, pnlNum)
            };

            await addDoc(entriesCol, entry);

            if (addAnother) {
                // A fresh form for the same day, account and entry type.
                setFormData({ title: '', pnl: '', rr: '', notes: '' });
                setScreenshots([]);
                toast.success('Saved. Ready for the next entry.');
                requestAnimationFrame(() => {
                    document.getElementById(isTape ? 'fast-tape-title' : 'fast-pnl')?.focus();
                });
                return;
            }

            const returnPath = location.state?.returnPath || '/trades';
            const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayCardPayload = {
                dateKey,
                dateLabel: new Date(year, month - 1, day).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }),
                pnl: pnlNum,
                rr: isTape ? null : Number(formData.rr),
                percentage: null,
                session: null,
                grade: null,
            };
            navigate(returnPath, { state: { showDayCardToast: true, dayCardPayload } });
        } catch (error) {
            console.error("Error saving entry:", error?.message || error);
            toast.error("Couldn't save this entry. Check your connection and try again.");
        } finally {
            setUploading(false);
        }
    };

    const openJournalMode = (journalMode) => {
        const returnPath = location.state?.returnPath || '/trades';
        const [year, month, day] = selectedDate.split('-').map(Number);
        navigate(`/day/${year}/${month}/${day}`, {
            state: { 
                date: new Date(year, month - 1, day).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
                directAdd: true,
                journalMode,
                returnPath: returnPath
            } 
        });
    };

    const handleGoToAdvanced = () => leaveIfConfirmed(() => openJournalMode('advanced'));
    const handleGoToBeta = () => leaveIfConfirmed(() => openJournalMode('beta'));

    const handleCancel = () => leaveIfConfirmed(() => {
        const returnPath = location.state?.returnPath || '/trades';
        navigate(returnPath);
    });

    const pnlTone = getPnlTone(formData.pnl);

    const panelClass = "theme-panel theme-border rounded-3xl border shadow-sm";
    const inputBase = "theme-input theme-border theme-text w-full border rounded-2xl transition-all";
    const labelClass = "theme-text-muted block text-[11px] font-semibold uppercase tracking-[0.22em]";
    const helperTextClass = "theme-text-muted text-sm";
    const minorSegmentGroupClass = "theme-choice-group rounded-xl p-0.5 flex items-center gap-0.5";
    const mainSegmentGroupClass = "theme-choice-group rounded-2xl p-1 flex items-center gap-1";
    const minorSegmentButtonBaseClass = "min-w-[56px] px-2.5 py-1 text-[11px] font-semibold leading-none rounded-lg border border-transparent transition-colors";
    // Below lg the workflow switch shares a full-width row, so its buttons size to their labels and split the spare width.
    const mainSegmentButtonBaseClass = "flex-auto whitespace-nowrap px-2 py-2 text-sm font-semibold rounded-xl border border-transparent transition-colors sm:px-4 lg:flex-none lg:min-w-[108px]";
    const segmentButtonActiveClass = "theme-accent-soft shadow-sm";
    const segmentButtonInactiveClass = "theme-text-muted theme-hover-surface";
    const insetSurfaceClass = "theme-panel-alt theme-border rounded-[28px] border";
    const subPanelClass = "theme-panel-alt theme-border rounded-[26px] border";
    const compactInputClass = `${inputBase} px-4 py-3 text-base sm:text-[15px]`;
    return (
        // Fixed, non-scrolling workspace only at xl, where the form and charts sit side by side;
        // below that the cards stack in one column and the page scrolls normally.
        <div data-testid="fast-page-shell" className="w-full min-h-screen xl:h-screen theme-app-shell flex flex-col xl:overflow-hidden font-sans">
            <style>{`
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                input[type=number] {
                    -moz-appearance: textfield;
                }
            `}</style>

            {/* --- HEADER --- */}
            {/* Below lg the switches wrap onto their own full-width row so the title keeps its width. */}
            <header className="min-h-14 lg:h-[60px] border-b theme-border theme-panel flex flex-wrap lg:flex-nowrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 sm:px-5 lg:px-6 lg:py-0 shrink-0 z-10">
                <div className="flex min-w-0 items-center gap-3">
                    <button
                        type="button"
                        aria-label="Go back"
                        onClick={handleCancel}
                        className="p-2 rounded-full transition-colors theme-text-muted theme-hover-surface"
                    >
                        <ArrowLeft size={22} />
                    </button>
                    <div className="flex min-w-0 flex-col">
                        <span className="truncate font-bold text-base sm:text-lg theme-text tracking-tight">
                            {entryMode === 'tape' ? 'Log Tape Reading' : 'Log Trade'}
                        </span>
                        <span className="truncate text-xs theme-text-muted uppercase tracking-wide">
                            {selectedAccount?.name || 'Trading Account'}
                        </span>
                    </div>
                </div>

                {/* Routing Buttons */}
                <div className="flex w-full items-end gap-3 lg:w-auto">
                    {/* Fast / Advanced / Beta toggle */}
                    <div
                        role="group"
                        aria-label="Entry workflow"
                        className={`${mainSegmentGroupClass} min-w-0 flex-1 lg:flex-none`}
                    >
                        <button 
                            type="button"
                            aria-pressed="true"
                            className={`${mainSegmentButtonBaseClass} ${segmentButtonActiveClass} pointer-events-none`}
                            style={{ borderColor: 'var(--theme-accent)' }}
                        >
                            Fast
                        </button>
                        <button 
                            type="button"
                            onClick={handleGoToAdvanced}
                            aria-pressed="false"
                            className={`${mainSegmentButtonBaseClass} ${segmentButtonInactiveClass}`}
                        >
                            Advanced
                        </button>
                        {unifiedJournalBeta && (
                            <button
                                type="button"
                                onClick={handleGoToBeta}
                                aria-pressed="false"
                                className={`${mainSegmentButtonBaseClass} ${segmentButtonInactiveClass}`}
                            >
                                Beta
                            </button>
                        )}
                    </div>

                    {/* Trade / Tape toggle */}
                    <div className="flex shrink-0 flex-col items-center gap-1">
                        <span className="text-[8px] font-semibold uppercase tracking-[0.12em] theme-text-muted text-center leading-none -translate-y-0.5">
                            Entry Type
                        </span>
                        <div
                            role="group"
                            aria-label="Entry type"
                            className={minorSegmentGroupClass}
                        >
                            <button
                                type="button"
                                onClick={() => setEntryMode('trade')}
                                aria-pressed={entryMode === 'trade'}
                                className={`${minorSegmentButtonBaseClass} ${entryMode === 'trade' ? segmentButtonActiveClass : segmentButtonInactiveClass}`}
                                style={entryMode === 'trade' ? { borderColor: 'var(--theme-accent)' } : undefined}
                            >
                                Trade
                            </button>
                            <button
                                type="button"
                                onClick={() => setEntryMode('tape')}
                                aria-pressed={entryMode === 'tape'}
                                className={`${minorSegmentButtonBaseClass} ${entryMode === 'tape' ? segmentButtonActiveClass : segmentButtonInactiveClass}`}
                                style={entryMode === 'tape' ? { borderColor: 'var(--theme-accent)' } : undefined}
                            >
                                Tape
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* --- MAIN CONTENT AREA --- */}
            <form ref={formRef} onSubmit={handleSubmit} className="flex-1 xl:h-full xl:overflow-hidden relative theme-app-shell">
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="w-full xl:h-full px-4 sm:px-6 lg:px-8 py-4 sm:py-5"
                    >
                        <div
                            data-testid="fast-main-grid"
                            className="grid gap-4 xl:h-full xl:grid-cols-[minmax(0,1fr)_300px] 2xl:grid-cols-[minmax(0,1.15fr)_320px]"
                        >
                            <div
                                data-testid="fast-form-column"
                                className="grid min-w-0 content-start gap-4 xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)]"
                            >
                                {entryMode === 'trade' ? (
                                    <>
                                        <div className="grid min-w-0 items-stretch gap-4 xl:grid-cols-[minmax(0,500px)_minmax(0,1fr)]">
                                            <section className={`${panelClass} min-w-0 p-4 sm:p-5`}>
                                                <div data-testid="fast-pnl-shell" className="grid min-w-0 max-w-[500px] gap-3">
                                                    <label htmlFor="fast-pnl" className={labelClass}>Net Profit / Loss</label>
                                                    <div
                                                        data-testid="fast-pnl-input-row"
                                                        data-pnl-tone={pnlTone}
                                                        className={`${insetSurfaceClass} pnl-tone-border theme-focus-within-accent min-w-0 overflow-hidden px-5 py-4 transition-all sm:px-6 sm:py-5`}
                                                    >
                                                        <div
                                                            data-pnl-tone={pnlTone}
                                                            className="pnl-tone-text theme-text flex min-w-0 items-end gap-3"
                                                        >
                                                            <span className="-translate-y-1 text-4xl font-semibold leading-none sm:text-5xl">$</span>
                                                            <input
                                                                id="fast-pnl"
                                                                type="number"
                                                                data-theme-input-mode="unstyled"
                                                                data-pnl-tone={pnlTone}
                                                                value={formData.pnl}
                                                                onChange={(e) => setFormData(prev => ({ ...prev, pnl: e.target.value }))}
                                                                className="pnl-tone-input w-full flex-1 min-w-0 border-0 bg-transparent p-0 text-left text-4xl font-semibold leading-none tracking-tight outline-none sm:text-5xl"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>

                                            <section className={`${panelClass} min-w-0 p-4 sm:p-5`}>
                                                <div className="grid gap-3 content-start">
                                                    <div className={`${subPanelClass} p-3`}>
                                                        <label htmlFor="fast-title" className={labelClass}>Title</label>
                                                        <input
                                                            id="fast-title"
                                                            type="text"
                                                            value={formData.title}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                                            placeholder="e.g. NQ long reversal"
                                                            className={`${compactInputClass} mt-2`}
                                                        />
                                                    </div>

                                                    <div className="grid gap-3 sm:grid-cols-[minmax(220px,1.15fr)_minmax(140px,0.85fr)]">
                                                        {renderDateField({ id: "fast-date", testId: "fast-date-display" })}

                                                        <div className={`${subPanelClass} p-3`}>
                                                            <label htmlFor="fast-rr" className={labelClass}>R:R Ratio</label>
                                                            <input
                                                                id="fast-rr"
                                                                type="number"
                                                                step="0.1"
                                                                value={formData.rr}
                                                                onChange={(e) => setFormData(prev => ({ ...prev, rr: e.target.value }))}
                                                                placeholder="2.0"
                                                                className={`${compactInputClass} mt-2`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        </div>

                                        <section className={`${panelClass} p-4 sm:p-5 min-h-0 flex flex-col`}>
                                            <label htmlFor="fast-trade-notes" className={labelClass}>Trade Notes</label>
                                            <textarea
                                                id="fast-trade-notes"
                                                value={formData.notes}
                                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                                placeholder="Execution, context, mistakes, what to improve..."
                                                className={`${compactInputClass} mt-3 flex-1 resize-none min-h-[160px] xl:min-h-0`}
                                            />
                                        </section>
                                    </>
                                ) : (
                                    <>
                                        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                            <section className={`${panelClass} p-4 sm:p-5`}>
                                                <div className="grid gap-3 content-start">
                                                    <div className={`${subPanelClass} p-3`}>
                                                        <label htmlFor="fast-tape-title" className={labelClass}>Title</label>
                                                        <input
                                                            id="fast-tape-title"
                                                            type="text"
                                                            value={formData.title}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                                            placeholder="e.g. NQ tape read"
                                                            className={`${compactInputClass} mt-2`}
                                                            autoFocus
                                                        />
                                                    </div>
                                                    {renderDateField({ id: "fast-tape-date", testId: "fast-tape-date-display" })}
                                                </div>
                                            </section>

                                            <section className={`${panelClass} p-4 sm:p-5 flex flex-col`}>
                                                <div className={labelClass}>Tape Reading</div>
                                                <p className={`${helperTextClass} mt-2`}>
                                                    Keep the read short, save the chart, and move on without hunting for fields.
                                                </p>
                                                <div className={`${insetSurfaceClass} mt-3 px-4 py-3 flex-1 flex flex-col justify-center`}>
                                                    <div className="theme-accent-soft theme-accent rounded-full w-fit px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase">
                                                        Fast Log
                                                    </div>
                                                    <p className={`${helperTextClass} mt-3`}>
                                                        Title and date sit beside the notes so you stay fast.
                                                    </p>
                                                </div>
                                            </section>
                                        </div>

                                        <section className={`${panelClass} p-4 sm:p-5 min-h-0 flex flex-col`}>
                                            <label htmlFor="fast-tape-notes" className={labelClass}>Tape Notes</label>
                                            <p className={`${helperTextClass} mt-1`}>
                                                Keep it short: what price did, where liquidity sat, and what mattered.
                                            </p>
                                            <textarea
                                                id="fast-tape-notes"
                                                value={formData.notes}
                                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                                placeholder="Enter your tape reading notes..."
                                                className={`${compactInputClass} mt-3 flex-1 resize-none min-h-[160px] xl:min-h-0`}
                                            />
                                        </section>
                                    </>
                                )}
                            </div>

                            <div
                                data-testid="fast-sidebar"
                                className="grid min-w-0 content-start gap-4 xl:min-h-0 xl:grid-rows-[minmax(0,1fr)_auto]"
                            >
                                <section data-testid="fast-screenshot-card" className={`${panelClass} p-3 sm:p-4 min-h-0 flex flex-col`}>
                                    <div className={`${insetSurfaceClass} relative p-2 flex-1 min-h-0`}>
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            {...chartDropZoneProps}
                                            onPaste={handlePasteImages}
                                            data-dragging={isDraggingChart ? 'true' : undefined}
                                            className={`theme-border relative flex h-full w-full flex-col rounded-[22px] border border-dashed cursor-pointer transition-all overflow-hidden ${screenshots.length > 0 ? 'theme-accent-soft' : 'theme-panel-alt theme-hover-surface'}`}
                                            style={isDraggingChart
                                                ? { ...CHART_DROP_HIGHLIGHT_STYLE, borderColor: 'var(--theme-accent)' }
                                                : screenshots.length > 0 ? { borderColor: 'var(--theme-accent)' } : undefined}
                                        >
                                            <input ref={fileInputRef} type="file" id="fast-upload" className="hidden" accept="image/*" multiple onChange={handleFileUpload} />
                                            {screenshots.length > 0 ? (
                                                <div className="grid min-h-0 w-full flex-1 grid-cols-2 content-start gap-2 overflow-y-auto p-2">
                                                    {screenshots.map((src, index) => (
                                                        <div key={`${src}-${index}`} className="relative min-h-20 overflow-hidden rounded-[16px]">
                                                            <img src={src} alt={`Preview ${index + 1}`} className="h-full w-full object-cover shadow-sm" />
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setScreenshots((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
                                                                }}
                                                                className="theme-panel absolute right-1.5 top-1.5 rounded-full p-1 text-red-500 shadow-sm"
                                                                aria-label={`Remove screenshot ${index + 1}`}
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="theme-text-muted flex min-h-0 flex-1 flex-col items-center justify-center text-center px-4 py-4">
                                                    <Upload className="w-7 h-7 mb-2" />
                                                    <span className="theme-text font-semibold text-sm">No charts attached yet</span>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            fileInputRef.current?.click();
                                                        }}
                                                        className="mt-2 rounded-lg px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                                                        style={{ backgroundColor: 'var(--theme-accent)' }}
                                                    >
                                                        Add Entry or Exit Chart
                                                    </button>
                                                    <span className="mt-2 text-xs">Click, drag, or paste an image here.</span>
                                                </div>
                                            )}
                                            <input
                                                type="text"
                                                aria-label="Paste screenshots"
                                                value=""
                                                onChange={() => {}}
                                                onClick={(e) => e.stopPropagation()}
                                                onPaste={handlePasteImages}
                                                placeholder="Click here, then Ctrl+V to paste images"
                                                className="theme-input theme-border theme-text mx-2 mb-2 min-w-0 self-stretch shrink-0 rounded-xl border px-3 py-2 text-xs"
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section data-testid="fast-actions-card" className={`${panelClass} p-3 sm:p-4`}>
                                    <div className="grid gap-2 grid-cols-[auto_auto_1fr] xl:grid-cols-[auto_1fr]">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="theme-btn-ghost order-1 px-4 py-2.5 rounded-2xl font-bold text-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={uploading || (entryMode === 'trade' && formData.pnl === '')}
                                            className="theme-btn-primary order-3 w-full font-bold text-sm sm:text-base px-4 py-2.5 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {uploading ? (
                                                <span className="animate-pulse">Syncing...</span>
                                            ) : (
                                                <>
                                                    <Save size={16} />
                                                    <span>{entryMode === 'tape' ? 'Save Tape' : 'Save Trade'}</span>
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => saveQuickEntry({ addAnother: true })}
                                            disabled={uploading || (entryMode === 'trade' && formData.pnl === '')}
                                            aria-label="Save and add another"
                                            title="Save and start a new entry for the same day"
                                            className="theme-btn-ghost order-2 flex items-center justify-center rounded-2xl px-3 py-2.5 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed xl:order-last xl:col-span-2 xl:py-1.5"
                                        >
                                            <Plus size={16} className="xl:hidden" aria-hidden="true" />
                                            <span className="hidden xl:inline">Save &amp; add another</span>
                                        </button>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

            </form>
        </div>
    );
};

export default QuickAddPage;
